/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { mergeMap } from 'rxjs/operators'
import { EMPTY, from, lastValueFrom, Observable, of, Subject } from 'rxjs'
import { WINDOW, WindowWrapper } from '../utils'
import { ConfigService } from '../config/config.service'
import { CustomLocaleLoaderService } from '../custom-locale-loader.service'
import { LocaleInformation } from '../config/config.interface'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'

export abstract class LocaleHandlingConfig {
  localeHeaderWhitelistUrl?: string | RegExp | (string | RegExp)[] | null
  localeHeaderNameCountry?: string
  localeHeaderNameLocale?: string
  translateLoaderProvider?: TranslateHttpLoader
}

@Injectable({
  providedIn: 'root',
})
export class LocaleHandlingService {
  private window = inject<WindowWrapper>(WINDOW)
  private localeHandlingConfig = inject(LocaleHandlingConfig, { optional: true })
  protected translate = inject(TranslateService)
  protected configService = inject(ConfigService)
  protected localeLoaderService = inject(CustomLocaleLoaderService)

  private readonly defaultConfig: LocaleHandlingConfig = {
    localeHeaderWhitelistUrl: null,
    localeHeaderNameCountry: 'x-vrs-url-country',
    localeHeaderNameLocale: 'x-vrs-url-language',
  }

  private readonly config: LocaleHandlingConfig
  private initializedSubject = new Subject<void>()
  private initializedPromise = this.initializedSubject.toPromise() as Promise<void>

  constructor() {
    this.config = {
      ...this.defaultConfig,
      ...this.localeHandlingConfig,
    }
  }

  check(
    localeInformation: LocaleInformation,
    validLocale: () => Promise<boolean>,
    invalidLocale: () => Promise<boolean>,
  ): Promise<boolean> {
    if (localeInformation && localeInformation.translationFileName && localeInformation.angularLocale) {
      return validLocale()
    }
    return invalidLocale()
  }

  async setup(): Promise<boolean> {
    const result = await this.check(
      this.configService.getLocaleInformation(),
      () => this.validLocalePromise(),
      () => this.invalidLocalePromise(),
    )

    this.initializedSubject.next()
    this.initializedSubject.complete()
    return result
  }

  initialized(): Promise<void> {
    return this.initializedPromise
  }

  getConfig(): LocaleHandlingConfig {
    return this.config
  }

  private async validLocalePromise(): Promise<boolean> {
    const angularLocale = this.configService.getAngularLocale()
    const fallbackTranslationFilename = this.configService.getFallbackTranslationFileName()

    this.update(angularLocale)

    const filename = this.configService.getTranslationFileName()
    await Promise.all([
      lastValueFrom(this.translate.use(filename)),
      this.loadFallbackTranslationsIfAvailable(fallbackTranslationFilename),
      this.loadAngularLocaleData(angularLocale),
    ])

    return true
  }

  private async invalidLocalePromise(): Promise<boolean> {
    this.window.location.href = this.configService.getInvalidLocaleRedirectUrl()
    return false
  }

  private async loadFallbackTranslationsIfAvailable(fallbackTranslationFilename: string): Promise<string> {
    return this.queue(fallbackTranslationFilename)
      .pipe(mergeMap((locale) => from(this.loadFallbackTranslations(locale))))
      .toPromise() as Promise<string>
  }

  queue(fallback: string): Observable<string> {
    if (fallback) {
      return of(fallback)
    }

    return EMPTY
  }

  update(locale: string) {
    const htmlElement: HTMLHtmlElement = document.querySelector('html')!
    htmlElement.lang = locale
  }

  private async loadFallbackTranslations(locale: string): Promise<string> {
    /**
     * preload translations because setDefaultLang doesn't provide
     * an observable that tells if the translations are ready.
     * however, if they are already loaded it's synchronous
     */
    await this.translate.get(locale).toPromise()
    this.translate.setFallbackLang(locale)
    return locale
  }

  private async loadAngularLocaleData(angularLocale: string): Promise<string> {
    return this.localeLoaderService.load(angularLocale).toPromise() as Promise<string>
  }
}
